import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  VStack,
  Stack,
  HStack,
  useColorModeValue,
  FormErrorMessage,
  IconButton,
} from "@chakra-ui/react";
import { useRedeemUserRewardMutation } from "../../slices/adminApiSlice";
import { useGetUserDataMutation, useGetDecryptRewardQrCodeMutation } from "../../slices/userApiSlice";
import Loader from "../../components/Loader";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef, useMemo } from "react";
import { ToastService } from "../../services/toast/toastService";
import QrScanner from "react-qr-scanner";
import { FaQrcode, FaTimes } from "react-icons/fa";

const RedeemptionByPoints = () => {
  const { adminInfo } = useSelector((state) => state.adminAuth);
  const navigate = useNavigate();
  const [redeemUserReward, { isLoading }] = useRedeemUserRewardMutation();
  const [decryptData] = useGetDecryptRewardQrCodeMutation();
  const [getUserData] = useGetUserDataMutation();
  const [formData, setFormData] = useState({
    adminId: "",
    userId: "",
    rewardId: "",
    invoiceId: "",
    amount: 0,
    points: 0,
  
  });
  const [formErrors, setFormErrors] = useState({});
  const { success, error } = ToastService();
  const bgColor = useColorModeValue("white", "gray.700");
  const currentAdminId = adminInfo ? adminInfo._id : null;
  const [isReadOnly, setIsReadOnly] = useState(false);
  //const [userData, setUserData] = useState(null);
  const [userData, setUserData] = useState({
    current_points: 0,
  });
  // const [isScanning, setIsScanning] = useState(false);
  const [isQrScannerVisible, setQrScannerVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const invoiceInputRef = useRef(null);
  const MIN_AMOUNT = 10000;

  const safeTrim = (value) => {
    return typeof value === 'string' ? value.trim() : '';  // Return empty string if not a valid string
  };

  const isFormValid = useMemo(() => {
  const amount = Number(formData.amount);
  const points = Number(formData.points);

    return (
      safeTrim(formData.userId) !== "" &&
      safeTrim(formData.invoiceId) !== "" &&
      amount >= MIN_AMOUNT &&
      points > 0 &&
      userData?.current_points >= points
    );
  }, [formData, userData]);

  
  // const isFormValid = () => {
  //   return (
  //     safeTrim(formData.userId) !== '' &&
  //     safeTrim(formData.invoiceId) !== '' &&
  //     formData.amount > 10000 &&
  //     formData.points > 0 &&
  //     userData?.current_points >= formData.points // Ensure current points are sufficient
  //   );
  // };

  useEffect(() => {
    if (adminInfo) {
      const formattedDate = formatDate();
      // Get the first character of adminInfo.shop_code
      const shopCode = adminInfo.shop_code ? adminInfo.shop_code : '';
        
      // Generate the invoiceId based on the shop code and formatted date
      const invoiceId = `${shopCode}-RD${formattedDate}0000`;

      setFormData((prevData) => ({
          ...prevData,
          adminId: currentAdminId, // Set adminId to currentAdminId
          invoiceId: invoiceId,
      }));
    }
  }, [adminInfo, currentAdminId]);

  // Using useRef to persist the timeout across renders
  const typingTimeout = useRef(null);
  // To track previous value of invoiceId, use useRef to keep it persistent across renders
  const prevInvoiceId = useRef('');

  const handleQrCodeButton = () => {
    setQrScannerVisible(!isQrScannerVisible);

    // After the state change, focus the input element
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Check if the field is invoiceId and whether it has changed
    if (name === 'invoiceId' && value === prevInvoiceId.current) {
      return; // Skip fetch if invoiceId hasn't changed
    }

    // Update the previous value for invoiceId (for future comparisons)
    if (name === 'invoiceId') {
      prevInvoiceId.current = value;
    }

    // If it's a different field (not invoiceId), perform the fetch
    if (name !== 'invoiceId') {
      // Clear the previous timeout (if any)
      clearTimeout(typingTimeout.current);

      // Set a new timeout to update user data after 2 second of inactivity
      typingTimeout.current = setTimeout(async () => {
        try {
          const encryptedData  = value;
          const response = await decryptData(encryptedData).unwrap();
          
          // Check if required fields are present
          if (!response || !response.userId || !response.rewardId || !response.pointsRequired) {
            // Optionally, handle missing fields with some validation logic
            setFormErrors({ ...formErrors, userId: 'Missing required fields in QR code data.' });
            return;
          }

          // Fetch user data
          const userResponse = await getUserData(response.userId).unwrap();
          // Set user data and form data
          setUserData(userResponse.user);

          // Update form data state
          setFormData({
            adminId: currentAdminId,
            userId: response.userId,
            rewardId: response.rewardId,
            points: response.pointsRequired,
          });
          
        } catch (error) {
          // Handle invalid JSON format
          setFormErrors({ ...formErrors, userId: 'Invalid JSON data. Please scan the QR code again.' });
        }
      }, 1000);
    }
    // Track the previous value of invoiceId after processing the change
    if (name === 'invoiceId') {
      prevInvoiceId.current = value;
    }
  };  

  // Function to format the date to YYMMDD
  const formatDate = () => {
    const now = new Date();
    const year = now.getFullYear().toString().slice(2); // Get last 2 digits of the year (e.g., 2025 -> 25)
    const month = (now.getMonth() + 1).toString().padStart(2, '0'); // Get month (01-12)
    const day = now.getDate().toString().padStart(2, '0'); // Get day (01-31)

    return `${year}${month}${day}`; // Format as YYMMDD
  };

  const handleNumberChange = (valueStr, valueNum, fieldName) => {
    // Always update form data so the input isn't blocked
    setFormData(prev => ({ ...prev, [fieldName]: valueStr }));
  
    if (fieldName === "amount") {
      const numericValue = Number(valueStr);
  
      if (isNaN(numericValue) || numericValue < 10000) {
        setFormErrors(prev => ({
          ...prev,
          amount: "Amount must be at least 10,000"
        }));
      } else {
        setFormErrors(prev => ({ ...prev, amount: "" }));
      }
    }
  };  

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid) {
      setErrorMessage("Please fill in all required fields.");
      return; // ⛔ stop submit
    }

    setErrorMessage(null);
    setLoading(true);

    try {
      const response = await redeemUserReward({ ...formData }).unwrap();
      success(response.message);

      const formattedDate = formatDate();
      const shopCode = adminInfo.shop_code || "";
      const invoiceId = `${shopCode}-RD${formattedDate}0000`;

      setFormData({
        userId: "",
        rewardId: "",
        invoiceId,
        amount: 0,
        points: 0,
      });

      // ❗ Optional: do NOT hard reset points unless backend confirms it
      // Better to refetch user data instead
      setUserData((prev) => ({
        ...prev,
        current_points: prev.current_points - formData.points,
      }));
    } catch (err) {
      error(err?.data?.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };


  // const handleSubmit = async (e) => {
  //   e.preventDefault();

  //   if (!isFormValid()) {
  //     setErrorMessage('Please fill in all required fields.');
  //   } else {
  //     setErrorMessage(null); // Reset error message when form is valid
  //   }

  //   // Start loading when form submission begins
  //   setLoading(true);
  //   try {
  //     // Perform the asynchronous action (e.g., redeeming the reward)
  //     const response = await redeemUserReward({ ...formData }).unwrap();

  //     if (response.status >= 400) {
  //       // console.error("Error response:", response);
  //       error(response.message || 'Failed to redeem the reward. Please try again.');
  //     } else {
  //       success(response.message);
  //       const formattedDate = formatDate();
  //       // Get the first character of adminInfo.shop_code
  //       const shopCode = adminInfo.shop_code ? adminInfo.shop_code : '';
          
  //       // Generate the invoiceId based on the shop code and formatted date
  //       const invoiceId = `${shopCode}-RD${formattedDate}0000`;
  //       setFormData({ userId: "", rewardId: "", invoiceId: invoiceId, amount: 0, points: 0 });

  //       // Reset the current points to 0
  //       setUserData(prevData => ({ ...prevData, current_points: 0 }));

  //     }
  //   } catch (err) {
  //     // Handle any errors during the async operation
  //     error(err.data?.message || "An unexpected error occurred.");
  //   } finally {
  //     // Always set loading to false when the operation completes
  //     setLoading(false);
  //   }
  // };

  const handleError = (err) => {
    // console.error(err);
    setErrorMessage("Failed to scan. Please try again.");
  };

  const handleScan = async (data) => {
    if (!data) return;
  
    // setIsScanning(true);
    try {
      const encryptedData  = data.text;

      const response = await decryptData(encryptedData).unwrap();
          
      // Check if required fields are present
      if (!response || !response.userId || !response.rewardId || !response.pointsRequired) {
        // Optionally, handle missing fields with some validation logic
        setFormErrors({ ...formErrors, userId: 'Missing required fields in QR code data.' });
        return;
      }
  
      // Fetch user data
      const userResponse = await getUserData(response.userId).unwrap();

      // Check for API error response (status >= 400)
      if (userResponse?.status >= 400) {
        error(userResponse.message || 'Failed to fetch user data');
      }

      // Check if user data exists
      if (!userResponse?.user) {
        error('User data not found in response');
      }
      
      // Set user data and form data
      setUserData(userResponse.user);
      setFormData({
        adminId: currentAdminId,
        userId: response.userId,
        rewardId: response.rewardId,
        points: response.pointsRequired,
      });
  
      setIsReadOnly(true);
      setErrorMessage('');
      // After the state change, focus the invoice Id input element
      if (invoiceInputRef.current) {
        invoiceInputRef.current.focus();
      }
  
    } catch (error) {
      // console.error("Error during scan:", error);
      const errorMessage = error.data?.message || error.message || "An unexpected error occurred.";
      setErrorMessage(errorMessage);
    }
    setQrScannerVisible(false); // Optionally hide the QR scanner after scan
  };  

  return (
    <>
      <Stack 
        w={'full'}
        bg={bgColor}
        spacing={6} 
        maxW={"full"} 
        rounded={"lg"} 
        boxShadow={'2xl'} 
        p={8} 
        borderWidth={'thin'}
        >
        <form onSubmit={handleSubmit}>
          {isLoading ? (
            <Loader />
          ) : (
            <VStack spacing={2} align="stretch">
              <Stack
                justify="center" 
                align="center"
                >

                {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}
                
                {/* Add Toggle Button for QR Scanner */}
                <IconButton
                    icon={isQrScannerVisible ? <FaTimes /> : <FaQrcode />}
                    onClick={handleQrCodeButton}
                    aria-label="Toggle QR Scanner"
                    colorScheme="teal"
                    variant={'outline'}
                    width="full"
                    mt={4}
                    _hover={{ bg: 'teal.500', color: 'white' }}
                  />

                  {/* Conditionally render the QR scanner */}
                  {isQrScannerVisible && (
                    <Box mt={4} borderWidth={1} borderRadius="md" p={4}>
                        <QrScanner 
                          id="qr-scanner"
                          //facingMode="environment"  // Request back camera
                          onError={handleError}
                          onScan={(data) => {
                            handleScan(data);
                          }}
                          delay={300}
                        />
                    </Box>
                  )}
              
              </Stack>
              <HStack>
                <Box w={'full'}>
                  <FormControl isRequired isInvalid={!!formErrors.userId}>
                    <FormLabel>Reward Qr Code</FormLabel>
                    <Input 
                      name="userId" 
                      placeholder="User ID" 
                      value={formData.userId} 
                      onChange={handleChange} 
                      readOnly={isReadOnly} 
                      autoFocus
                      ref={inputRef}
                    />
                    <FormErrorMessage>{formErrors.userId}</FormErrorMessage>
                  </FormControl>
                </Box>
                <Box w={'full'}>
                <FormControl isRequired isInvalid={!!formErrors.rewardId}>
                  <FormLabel>Reward ID</FormLabel>
                  <Input name="rewardId" placeholder="Reward ID" value={formData.rewardId} onChange={handleChange} readOnly={isReadOnly} />
                  <FormErrorMessage>{formErrors.rewardId}</FormErrorMessage>
                </FormControl>
                </Box>
              </HStack>

              <HStack>
                <Box w={'full'}>
                  <FormControl isRequired isInvalid={!!formErrors.invoiceId}>
                    <FormLabel>Invoice ID</FormLabel>
                    <Input name="invoiceId" ref={invoiceInputRef} placeholder="Enter Invoice ID" value={formData.invoiceId} onChange={handleChange} />
                    <FormErrorMessage>{formErrors.invoiceId}</FormErrorMessage>
                  </FormControl>
                </Box>
                <Box w={'full'}>
                <FormControl isRequired isInvalid={!!formErrors.amount}>
                  <FormLabel>Amount</FormLabel>
                    <NumberInput 
                      min={0}
                      value={formData.amount}
                      onChange={(valueStr, valueNum) => handleNumberChange(valueStr, valueNum, "amount")}
                    >
                    <NumberInputField name="amount" />
                  </NumberInput>
                  <FormErrorMessage>
                    {formErrors.amount || "Amount must be at least 10,000"}
                  </FormErrorMessage>
                </FormControl>
              </Box>
              </HStack>
              <HStack>
                <Box w={'full'}>
                  <FormControl isRequired isInvalid={!!formErrors.points}>
                    <FormLabel>Points Required</FormLabel>
                    <NumberInput
                      min={1}
                      readOnly={isReadOnly}
                      value={formData.points}
                      onChange={(valueStr, valueNum) => {
                        if (valueStr.length <= 3) {
                          handleNumberChange(valueStr, valueNum, "points");
                        }
                      }}
                      >
                      <NumberInputField name="points" />
                    </NumberInput>
                    <FormErrorMessage>{formErrors.points}</FormErrorMessage>
                  </FormControl>
                </Box>
                <Box w={'full'}>
                  <FormControl isRequired isInvalid={!!formErrors.points}>
                    <FormLabel>Current Points</FormLabel>
                    <NumberInput
                      min={1}
                      readOnly={isReadOnly}
                      value={userData?.current_points}
                      style={{
                        backgroundColor: userData?.current_points < formData.points ? 'lightcoral' : 'initial'
                      }}
                      defaultValue={'0'}
                    >
                      <NumberInputField name="currentPoints" />
                    </NumberInput>
                  </FormControl>
                </Box>
              </HStack>
              <Stack spacing={2} direction={['column', 'row']}>
                 {/* Error message */}
              {/* {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>} */}

              {/* Submit Button with loading state */}
              <Button
                type="submit"
                colorScheme="teal"
                width="full"
                mt={4}
                isDisabled={!isFormValid || loading}  // Disable if form is invalid or if loading
                isLoading={loading}  // Show loading spinner when submitting
              >
                Redeem
              </Button>
                <Button 
                  width="full" 
                  mt={4}
                  //_hover={{ bg: 'gray.500', color: 'white' }} 
                  onClick={() => navigate(-1)} 
                  colorScheme="gray" 
                 >
                  Cancel
                </Button>
              </Stack>
            </VStack>
          )}
        </form>  
      </Stack>
    </>
  );
};

export default RedeemptionByPoints;